import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Pencil,
  Printer,
  RefreshCw,
  ShoppingCart,
  X,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { toast } from "sonner";

import logo from "@/assets/logo-light.png";
import { AvatarPicker } from "@/components/avatar-picker";
import {
  type Amendment,
  AmendmentDialog,
  PrintDialog,
  useAllowedAmendments,
} from "@/components/member-actions";
import { type HistoryKey, MemberHistories } from "@/components/member-histories";
import { EditDialog, type FieldDef } from "@/components/resource-page";
import { Badge } from "@/components/ui/badge";
import { Button, IconButton } from "@/components/ui/button";
import { Modal } from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Field, Input } from "@/components/ui/field";
import { Select } from "@/components/ui/select";
import { Skeleton, SkeletonLines } from "@/components/ui/skeleton";
import { Tooltip } from "@/components/ui/tooltip";
import { api, errorMessage, ifMatch } from "@/services/api";
import type { TranslationKey } from "@/constants/i18n";
import { useCan } from "@/utils/permissions";
import { cn } from "@/utils/utils";
import {
  ACCOUNT_TYPE,
  ACQUISITION,
  GENDER,
  MEMBERSHIP_TYPE,
  type Member,
  PREFIX,
  STATUS,
  SUFFIX,
  TIER,
  day,
  fields,
  fullName,
  label,
  toForm,
} from "@/pages/MembersPage";
import { useTranslation } from "@/store/locale";

const MASK = "********";

/** How each account status reads at a glance, as the legacy badge coloured them. */
const STATUS_TONE: Record<string, "success" | "danger" | "warning" | "neutral"> = {
  active: "success",
  expired: "danger",
  blacklisted: "warning",
  cancelled: "neutral",
};

/**
 * The cards a member is edited by, one dialog each — the legacy detail page
 * had a modal per card, and a forty-field form is nobody's idea of an edit.
 * Each names the fields on its card and the route that takes exactly those.
 */
type Card = "basic" | "business" | "consent" | "acquisition" | "card";
const CARDS: Record<Card, { action: string; title: TranslationKey; names: string[] }> = {
  basic: {
    action: "basic-info",
    title: "md.basicInformation",
    names: [
      "prefix",
      "suffix",
      "first_name",
      "middle_name",
      "last_name",
      "gender",
      "date_of_birth",
      "mobile_number",
      "email",
      "unit_number",
      "building_name",
      "address_street",
      "city_id",
      "barangay_id",
      "employee_number",
      "company_name",
    ],
  },
  business: {
    action: "business-info",
    title: "md.businessInformation",
    names: ["business_name", "business_company_name", "business_type", "position"],
  },
  consent: {
    action: "consent",
    title: "md.membersConsent",
    names: [
      "email",
      "mobile_number",
      "email_consent",
      "phone_consent",
      "only_important_updates",
    ],
  },
  acquisition: {
    action: "acquisition-type",
    title: "mem.acquisitionType",
    names: ["acquisition_type", "temporary_card_number"],
  },
  card: {
    action: "card-info",
    title: "md.cardInformation",
    names: ["valid_until", "no_expiry", "temporary_card_number"],
  },
};

/** The tiers a supplementary card can be issued under; the others have no range for one. */
const TAKES_SUPPLEMENTARY = new Set(["member", "complimentary"]);

function MemberDetailPage() {
  const { memberId } = useParams() as { memberId: string };
  return <MemberDetail id={Number(memberId)} />;
}

/**
 * The Detail View.
 *
 * `embedded` is for the members list, which takes the page over and draws the
 * standard header itself — the way back, the id, Refresh, Delete and Edit. The
 * body then brings no chrome of its own, or the page carries two of everything.
 * Standalone at `/members/$memberId`, it brings its own breadcrumb.
 */
export function MemberDetail({
  id,
  onClose,
  embedded = false,
}: {
  id: number;
  onClose?: () => void;
  embedded?: boolean;
}) {
  const t = useTranslation();
  const queryClient = useQueryClient();
  const member = useQuery({
    queryKey: ["/members", id],
    queryFn: async ({ signal }) => (await api.get<Member>(`/members/${id}`, { signal })).data,
  });
  const [card, setCard] = useState<Card | null>(null);
  const [tab, setTab] = useState<HistoryKey>("card");
  const [showBasic, setShowBasic] = useState(false);
  const [showBusiness, setShowBusiness] = useState(false);
  const [showCoBrand, setShowCoBrand] = useState(false);
  const [amendment, setAmendment] = useState<Amendment | null>(null);
  const [printing, setPrinting] = useState(false);
  const [tagging, setTagging] = useState(false);
  const [changingAccount, setChangingAccount] = useState<string | null>(null);
  const allowed = useAllowedAmendments(id);
  const allow = useCan();
  const canChange = allow("change__member");

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["/members", id] });
    void queryClient.invalidateQueries({ queryKey: [`/members/${id}/`], exact: false });
  };
  // Its own endpoint: an ordinary edit should not carry a few hundred kilobytes
  // of photograph on a path that has nothing to do with one.
  const picture = useMutation({
    mutationFn: (profile_image: string) =>
      api.put(`/members/${id}/profile-image`, { profile_image }),
    onSuccess: (_, value) => {
      toast.success(value ? t("md.photoUpdated") : t("md.photoRemoved"));
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error, t("md.photoFailed"))),
  });
  const volume = useMutation({
    mutationFn: (volume_shopper: boolean) =>
      api.patch(`/members/${id}/volume-shopper`, { volume_shopper }, ifMatch(member.data)),
    onSuccess: () => {
      toast.success(t("md.volumeShopperUpdated"));
      setTagging(false);
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error, t("md.updateFailed"))),
  });
  // An amendment, not an edit: the card is reissued under the new primary's
  // sequence and the history written, which a plain PATCH would skip.
  const changeAccount = useMutation({
    mutationFn: (parent_account_number: string) =>
      api.post(`/members/${id}/amendments/change-primary-account`, { parent_account_number }),
    onSuccess: () => {
      toast.success(t("md.primaryUpdated"));
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error, t("md.accountChangeFailed"))),
  });

  // The page holds its own shape while it loads, so nothing jumps into place.
  if (member.isPending)
    return (
      <div className={cn(embedded ? "" : "px-5 pb-10 md:px-8")} aria-busy>
        <Skeleton className="rounded-card mt-8 h-60" />
        <div className="mt-[-3.5rem] flex items-end gap-6 px-6">
          <Skeleton className="size-40 shrink-0 rounded-full" />
          <div className="grid flex-1 gap-2 pb-3">
            <Skeleton className="h-7 w-64" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>
        <div className="mt-8 grid gap-8 lg:grid-cols-[16rem_minmax(0,1fr)_17rem]">
          <SkeletonLines rows={7} />
          <SkeletonLines rows={10} />
          <SkeletonLines rows={6} />
        </div>
      </div>
    );
  if (!member.data)
    return (
      <EmptyState
        kind="error"
        title={t("md.notFound")}
        description={t("md.notFoundBody")}
        action={
          <Button asChild variant="secondary">
            <Link to="/members">{t("md.backToMembers")}</Link>
          </Button>
        }
      />
    );
  const m = member.data;
  const masked = (value: string | null | undefined) => (showBasic ? (value ?? "—") : MASK);
  const title = [
    label(MEMBERSHIP_TYPE(t), m.membership_type),
    label(TIER(t), m.membership_tier),
  ]
    .filter((t) => t !== "—")
    .join(" ");
  const status = label(STATUS(t), m.account_status);
  // Why the card cannot be printed right now, if it cannot — the same reasons
  // legacy showed on the disabled button, so the clerk knows what to fix.
  const printBlock = !m.profile_image
    ? t("md.printNeedsPhoto")
    : m.account_status !== "active"
      ? t("md.printNotActive").replace("{status}", status.toLowerCase())
      : m.is_temporarily_active
        ? t("md.printTemporary")
        : null;

  // The fields of the open card, in the card's order, sans the section
  // headings a one-card form has no use for. The tier decides two of them.
  const cardFields = (which: Card): FieldDef[] => {
    const all = fields(t);
    return CARDS[which].names.flatMap((name) => {
      const field = all.find((f) => f.name === name);
      if (!field) return [];
      if (name === "employee_number" && m.membership_tier !== "employee") return [];
      if (name === "company_name" && m.membership_tier !== "affiliates") return [];
      const { section: _section, ...rest } = field;
      // A member is enrolled over the counter or by a canvasser; "benefits" is
      // the other three tiers' channel and not a choice here, as legacy had it.
      if (name === "acquisition_type" && m.membership_tier === "member")
        return [{ ...rest, options: ACQUISITION(t).filter((o) => o.value !== "benefits") }];
      return [rest];
    });
  };

  return (
    // `Page` already ends with its own padding, so embedded adds none.
    <div className={embedded ? "" : "px-5 pb-10 md:px-8"}>
      <nav
        className={cn(
          "text-fg-muted text-callout flex items-center gap-2 py-4",
          embedded && "hidden",
        )}
      >
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            className="text-fg-subtle hover:bg-surface-inset hover:text-fg ml-auto grid size-9 place-items-center rounded-lg transition-colors"
          >
            <X className="size-5" aria-hidden />
            <span className="sr-only">{t("common.close")}</span>
          </button>
        ) : (
          <>
            <Link to="/members" className="text-fg hover:text-accent mr-2">
              <ArrowLeft className="size-5" aria-hidden />
              <span className="sr-only">{t("rp.back")}</span>
            </Link>
            <span>{t("shell.system")}</span>
            <ChevronRight className="size-3.5" aria-hidden />
            <Link to="/members" className="hover:text-accent">
              {t("nav.members")}
            </Link>
            <ChevronRight className="size-3.5" aria-hidden />
            <span className="text-fg">{t("page.memberDetail")}</span>
          </>
        )}
      </nav>

      {/* banner: the card's own navy, a gold sash for a gold membership */}
      <div className="relative">
        <div className="rounded-card bg-brand-700 relative h-60 overflow-hidden">
          {m.membership_type === "business" ? (
            <div className="absolute inset-x-0 top-1/2 h-16 -translate-y-1/2 -rotate-3 bg-[linear-gradient(90deg,transparent,#9aa5b1,#e5e9ee,#9aa5b1,transparent)] opacity-60" />
          ) : (
            <div className="absolute inset-x-0 top-1/2 h-16 -translate-y-1/2 -rotate-3 bg-[linear-gradient(90deg,transparent,#d4a72c,#f3d27a,#d4a72c,transparent)] opacity-90" />
          )}
          <img src={logo} alt="S&R" className="absolute top-6 left-6 h-10 w-auto" />
          <div className="absolute right-6 bottom-6 text-right text-white">
            <p className="text-callout font-medium tracking-wide">{m.card_number ?? "—"}</p>
            <p className="text-title-1 font-bold text-[#feffa3]">{title}</p>
            <p className="text-callout opacity-90">{label(ACCOUNT_TYPE(t), m.account_type)}</p>
          </div>
        </div>
        <div className="mt-[-3.5rem] flex flex-wrap items-end gap-6 px-6">
          <div className="relative shrink-0">
            {/* The same picker an employee's photograph uses: a file or the
                camera, framed before it is saved. The ID card stands in when
                there is no photograph, but only a photograph is ever written. */}
            <AvatarPicker
              src={m.profile_image ?? m.id_card_image}
              name={fullName(m) || t("mem.singular")}
              className="border-surface-raised size-40 rounded-full border-4 shadow-md"
              busy={picture.isPending}
              editable={canChange}
              onChange={(dataUrl) => picture.mutate(dataUrl)}
              onRemove={m.profile_image ? () => picture.mutate("") : undefined}
            />
            {/* The volume-shopper mark: lit when the member is one, and the
                way to change it for whoever may. */}
            <Tooltip
              label={
                !canChange
                  ? t("md.noPermission")
                  : m.volume_shopper
                    ? t("md.volumeShopperOn")
                    : t("md.volumeShopperOff")
              }
              side="bottom"
              className="absolute right-1 bottom-1"
            >
              <button
                type="button"
                disabled={!canChange}
                onClick={() => setTagging(true)}
                aria-pressed={m.volume_shopper}
                className={cn(
                  "border-surface-raised grid size-11 place-items-center rounded-full border-2 text-white shadow-md transition-colors",
                  m.volume_shopper
                    ? "bg-ink-900 hover:bg-ink-800"
                    : "bg-ink-400 hover:bg-ink-500",
                  !canChange && "cursor-not-allowed opacity-70",
                )}
              >
                <ShoppingCart className="size-5" aria-hidden />
                <span className="sr-only">{t("mem.volumeShopper")}</span>
              </button>
            </Tooltip>
          </div>
          <div className="min-w-0 flex-1 pb-2">
            <h1 className="text-heading text-[1.875rem] leading-[2.375rem] font-semibold uppercase">
              {fullName(m)}
            </h1>
            <p className="text-fg-muted text-body mt-1 flex flex-wrap items-center gap-3 font-bold">
              {t("md.accountNumber")} {m.account_number}
              <Badge tone={STATUS_TONE[m.account_status] ?? "neutral"}>{status}</Badge>
              {m.is_temporarily_active ? (
                <Badge tone="info">{t("mem.temporarilyActive")}</Badge>
              ) : null}
            </p>
          </div>
          <div className="flex items-center gap-2 pb-2">
            {canChange ? (
              <Tooltip label={printBlock} side="bottom">
                <Button size="sm" disabled={!!printBlock} onClick={() => setPrinting(true)}>
                  <Printer aria-hidden /> {t("md.print")}
                </Button>
              </Tooltip>
            ) : null}
            <IconButton label={t("rp.refresh")} variant="secondary" onClick={refresh}>
              <RefreshCw aria-hidden />
            </IconButton>
          </div>
        </div>
      </div>

      {/* three columns */}
      <div className="mt-8 grid gap-8 lg:grid-cols-[16rem_minmax(0,1fr)_17rem]">
        <dl className="text-callout grid content-start gap-5">
          <Item
            label={t("mem.acquisitionType")}
            onEdit={
              canChange && m.membership_tier === "member"
                ? () => setCard("acquisition")
                : undefined
            }
          >
            <Badge tone="brand">{label(ACQUISITION(t), m.acquisition_type)}</Badge>
          </Item>
          <Item label={t("mem.accountType")}>{label(ACCOUNT_TYPE(t), m.account_type)}</Item>
          <Item label={t("mem.memberSince")}>{day(m.created_at)}</Item>
          <Item label={t("mem.signupClub")}>{m.signup_club_name ?? t("md.na")}</Item>
          <Item label={t("mem.renewClub")}>{m.renew_club_name ?? t("md.na")}</Item>
          <Item label={t("mem.activeClub")}>{m.active_club_name ?? t("md.na")}</Item>
          <Item
            label={t("md.membersConsent")}
            onEdit={canChange ? () => setCard("consent") : undefined}
          >
            <span className="flex flex-wrap gap-1.5">
              {m.only_important_updates ? (
                <Badge tone="success">{t("md.onlyImportantUpdates")}</Badge>
              ) : null}
              {m.email_consent ? <Badge tone="success">{t("md.email")}</Badge> : null}
              {m.phone_consent ? <Badge tone="success">{t("md.phone")}</Badge> : null}
              {!m.only_important_updates && !m.email_consent && !m.phone_consent ? "—" : null}
            </span>
          </Item>
        </dl>

        <div className="grid content-start gap-8">
          <Group
            title={t("md.cardInformation")}
            onEdit={canChange ? () => setCard("card") : undefined}
          >
            <Pair k={t("mem.cardName")} v={m.card_name ?? fullName(m)} />
            <Pair k={t("mem.cardNumber")} v={m.card_number ?? t("md.na")} />
            <Pair k={t("mem.temporaryCardNumber")} v={m.temporary_card_number ?? t("md.na")} />
            <Pair
              k={t("md.validUntil")}
              v={m.no_expiry ? t("mem.noExpiry") : day(m.valid_until)}
            />
            <Pair k={t("md.cardStatus")} v={status} />
          </Group>
          <Group
            title={t("md.basicInformation")}
            onEdit={canChange ? () => setCard("basic") : undefined}
            shown={showBasic}
            onToggle={() => setShowBasic((s) => !s)}
          >
            <Pair k={t("mem.prefix")} v={masked(label(PREFIX(t), m.prefix))} />
            <Pair k={t("mem.suffix")} v={masked(label(SUFFIX(), m.suffix))} />
            <Pair k={t("mem.fullName")} v={masked(fullName(m))} />
            <Pair k={t("mem.mobileNumber")} v={masked(m.mobile_number)} />
            <Pair k={t("mem.emailAddress")} v={masked(m.email)} />
            <Pair k={t("mem.dateOfBirth")} v={showBasic ? day(m.date_of_birth) : MASK} />
            <Pair k={t("mem.gender")} v={masked(label(GENDER(t), m.gender))} />
            <Pair k={t("mem.unitNumber")} v={masked(m.unit_number)} />
            <Pair k={t("mem.buildingName")} v={masked(m.building_name)} />
            <Pair k={t("mem.addressStreet")} v={masked(m.address_street)} />
            <Pair k={t("mem.city")} v={masked(m.city_name)} />
            <Pair k={t("mem.barangay")} v={masked(m.barangay_name)} />
            {m.membership_tier === "employee" ? (
              <Pair k={t("mem.employeeNumber")} v={masked(m.employee_number)} />
            ) : null}
            {m.membership_tier === "affiliates" ? (
              <Pair k={t("mem.companyName")} v={masked(m.company_name)} />
            ) : null}
          </Group>
          {/* Behind a link, as legacy kept it: most members have no business
              on file, and four empty rows say less than one line. */}
          <section>
            <button
              type="button"
              onClick={() => setShowBusiness((s) => !s)}
              className="text-accent text-body font-medium hover:underline"
            >
              {showBusiness ? t("md.viewLess") : t("md.viewMore")}
            </button>
            {showBusiness ? (
              <div className="mt-4">
                <Group
                  title={t("md.businessInformation")}
                  onEdit={canChange ? () => setCard("business") : undefined}
                >
                  <Pair k={t("mem.businessName")} v={m.business_name ?? "—"} />
                  <Pair k={t("mem.businessCompanyName")} v={m.business_company_name ?? "—"} />
                  <Pair k={t("mem.businessType")} v={m.business_type ?? "—"} />
                  <Pair k={t("mem.position")} v={m.position ?? "—"} />
                </Group>
              </div>
            ) : null}
          </section>
        </div>

        <div className="text-callout grid content-start gap-6">
          {/* View only: attachments are put on a member by the system. */}
          <Item label={t("md.attachments")}>
            <Button variant="outline" onClick={() => setTab("attachments")}>
              {t("rp.view")}
            </Button>
          </Item>
          <Item label={t("mem.amendment")}>
            {/* Only what the server says this account can take right now. */}
            <Select
              value=""
              onChange={(e) => setAmendment((e.target.value || null) as Amendment | null)}
              disabled={!canChange || allowed.isPending || (allowed.data ?? []).length === 0}
            >
              <option value="">
                {(allowed.data ?? []).length === 0 && !allowed.isPending
                  ? t("md.noAmendments")
                  : t("md.selectAmendment")}
              </option>
              {(allowed.data ?? []).map((a) => (
                <option key={a} value={a}>
                  {t(`md.amendName.${a}` as TranslationKey)}
                </option>
              ))}
            </Select>
          </Item>
          {m.account_type === "supplementary" ? (
            <Item label={t("md.primaryAccountNo")}>
              <Input readOnly value={m.parent_account_number ?? ""} placeholder="—" />
              {canChange ? (
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-2"
                  onClick={() => setChangingAccount(m.parent_account_number ?? "")}
                >
                  {t("md.changeAccountNo")}
                </Button>
              ) : null}
            </Item>
          ) : TAKES_SUPPLEMENTARY.has(m.membership_tier ?? "") ? (
            <Item label={t("md.supplementaryAccount")}>
              <p className="text-fg-muted">
                {m.has_supplementary_created
                  ? t("md.hasSupplementary")
                  : t("md.noSupplementary")}
              </p>
              {/* Straight to the parent gate with this account filled in, so
                  the clerk is not asked to retype the number on the screen. */}
              {allow("add__member") && m.account_status === "active" ? (
                <Button asChild variant="secondary" size="sm" className="mt-2">
                  <Link to={`/members/new?tier=member&parent=${encodeURIComponent(m.account_number)}`}>
                    {t("md.addSupplementary")}
                  </Link>
                </Button>
              ) : null}
            </Item>
          ) : null}
          <Group
            title={t("md.coBrandTagging")}
            shown={showCoBrand}
            onToggle={() => setShowCoBrand((s) => !s)}
          >
            <Pair k={t("md.coBrand")} v={showCoBrand ? (m.co_brand_tagging ?? "—") : MASK} />
          </Group>
        </div>
      </div>

      <MemberHistories memberId={id} active={tab} onActive={setTab} />

      <AmendmentDialog
        key={amendment ?? "none"}
        memberId={id}
        action={amendment}
        currentTier={m.membership_tier}
        onClose={() => setAmendment(null)}
        onDone={() => {
          setAmendment(null);
          refresh();
        }}
      />
      <PrintDialog
        memberId={id}
        open={printing}
        onClose={() => setPrinting(false)}
        onPrinted={refresh}
      />

      <Modal
        open={tagging}
        onClose={() => setTagging(false)}
        size="sm"
        title={t("mem.volumeShopper")}
        description={t("md.volumeShopperBody")}
        footer={
          <>
            <Button variant="secondary" onClick={() => setTagging(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" form="volume-shopper" loading={volume.isPending}>
              {t("rp.save")}
            </Button>
          </>
        }
      >
        <VolumeShopperForm value={m.volume_shopper} onSubmit={(on) => volume.mutate(on)} />
      </Modal>

      {/* ponytail: one field; a picker once primary accounts can be searched. */}
      <Modal
        open={changingAccount !== null}
        onClose={() => setChangingAccount(null)}
        size="sm"
        title={t("md.changePrimaryTitle")}
        description={t("md.changePrimaryBody")}
        footer={
          <>
            <Button variant="secondary" onClick={() => setChangingAccount(null)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" form="account-no" loading={changeAccount.isPending}>
              {t("rp.save")}
            </Button>
          </>
        }
      >
        <form
          id="account-no"
          className="pb-2"
          onSubmit={(event) => {
            event.preventDefault();
            const next = changingAccount?.trim();
            if (next) changeAccount.mutate(next);
            setChangingAccount(null);
          }}
        >
          <Field label={t("md.primaryAccountNumber")} htmlFor="account-no-input">
            <Input
              id="account-no-input"
              autoFocus
              required
              value={changingAccount ?? ""}
              onChange={(event) => setChangingAccount(event.target.value)}
            />
          </Field>
        </form>
      </Modal>

      {card ? (
        <EditDialog
          singular={t(CARDS[card].title)}
          endpoint="/members"
          action={CARDS[card].action}
          fields={cardFields(card)}
          initial={toForm(m)}
          id={m.id}
          stamp={m.updated_at}
          columns={card === "basic" ? 2 : 1}
          onClose={() => setCard(null)}
          onSaved={() => {
            setCard(null);
            refresh();
          }}
        />
      ) : null}
    </div>
  );
}

/** Legacy's two radios: the flag is a decision, and a checkbox reads as a detail. */
function VolumeShopperForm({
  value,
  onSubmit,
}: {
  value: boolean;
  onSubmit: (on: boolean) => void;
}) {
  const t = useTranslation();
  const [on, setOn] = useState(value);
  return (
    <form
      id="volume-shopper"
      className="grid gap-3 pb-2"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(on);
      }}
    >
      {(
        [
          [true, t("md.enableVolumeShopper")],
          [false, t("md.disableVolumeShopper")],
        ] as const
      ).map(([choice, text]) => (
        <label key={String(choice)} className="text-fg text-body flex items-center gap-3">
          <input
            type="radio"
            name="volume_shopper"
            className="accent-accent size-4"
            checked={on === choice}
            onChange={() => setOn(choice)}
          />
          {text}
        </label>
      ))}
    </form>
  );
}

function Item({
  label: text,
  children,
  onEdit,
}: {
  label: string;
  children: ReactNode;
  onEdit?: (() => void) | undefined;
}) {
  return (
    <div>
      <dt className="text-fg-muted text-body flex items-center gap-2">
        {text}
        {onEdit ? <EditIcon onClick={onEdit} /> : null}
      </dt>
      <dd className="text-fg text-body mt-1.5">{children}</dd>
    </div>
  );
}

function Group({
  title,
  children,
  onEdit,
  shown,
  onToggle,
}: {
  title: string;
  children: ReactNode;
  onEdit?: (() => void) | undefined;
  shown?: boolean;
  onToggle?: () => void;
}) {
  const t = useTranslation();
  return (
    <section>
      <h3 className="text-fg text-body flex items-center gap-2 font-bold">
        {title}
        {onToggle ? (
          <button
            type="button"
            onClick={onToggle}
            className="text-fg-subtle hover:text-fg grid size-6 place-items-center"
          >
            {shown ? (
              <EyeOff className="size-4" aria-hidden />
            ) : (
              <Eye className="size-4" aria-hidden />
            )}
            <span className="sr-only">{shown ? t("md.hide") : t("md.show")}</span>
          </button>
        ) : null}
        {onEdit ? <EditIcon onClick={onEdit} /> : null}
      </h3>
      <dl className="text-body mt-3 grid gap-x-4 gap-y-3 sm:grid-cols-[10rem_minmax(0,1fr)]">
        {children}
      </dl>
    </section>
  );
}

function Pair({ k, v }: { k: string; v: string }) {
  return (
    <>
      <dt className="text-fg-muted">{k}</dt>
      <dd className="text-fg break-words">{v}</dd>
    </>
  );
}

function EditIcon({ onClick }: { onClick: () => void }) {
  const t = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-fg-subtle hover:text-fg grid size-6 place-items-center"
    >
      <Pencil className="size-4" aria-hidden />
      <span className="sr-only">{t("common.edit")}</span>
    </button>
  );
}

export default MemberDetailPage;
